import {Controller} from '@hotwired/stimulus';

/**
 * Titre d'une page placé « Dans la première ligne » : il s'affiche en titre 1 en tête de la première colonne de texte
 * (éditeur visuel) de l'éditeur de blocs, en lisant les lignes dans l'ordre. Le titre 1 et le champ « Titre de la page »
 * disent toujours la même chose : la saisie dans l'un met l'autre à jour, langue par langue.
 *
 * Le titre 1 est posé quand on choisit « Dans la première ligne », et, à la création (`insert`), dès qu'on tape le
 * titre ; sur une page existante, seul un titre 1 déjà en tête de la colonne est suivi, rien n'est ajouté à son contenu.
 * « En haut de la page » retire le titre 1 qui reprend le titre.
 *
 * Les champs sont reconnus à leur nom : `{item}[versions][{lang}][title]` et `{item}[versions][{lang}][titleInBlocks]`.
 */
export default class extends Controller {
  static values = {item: String, insert: Boolean};

  connect() {
    this.onInstanceReady = this.onInstanceReady.bind(this);
    window.CKEDITOR?.on('instanceReady', this.onInstanceReady);
    // les éditeurs déjà prêts (le contrôleur peut se connecter après eux)
    Object.values(window.CKEDITOR?.instances ?? {}).forEach((editor) => this.#listen(editor));
  }

  disconnect() {
    window.CKEDITOR?.removeListener('instanceReady', this.onInstanceReady);
  }

  titleChanged(event) {
    const lang = this.#lang(event.target, 'title');

    if (lang === null || !this.#inFirstLine(lang)) {
      return;
    }

    this.#writeHeading(lang, event.target.value, this.insertValue);
  }

  placementChanged(event) {
    const lang = this.#lang(event.target, 'titleInBlocks');

    if (lang === null) {
      return;
    }

    if (this.#inFirstLine(lang)) {
      this.#writeHeading(lang, this.#titleField(lang)?.value ?? '', true);
    } else {
      this.#removeHeading(lang);
    }
  }

  // une colonne de texte choisie après la saisie du titre reçoit le titre 1 à son ouverture
  onInstanceReady({editor}) {
    this.#listen(editor);

    const lang = this.#langOfEditor(editor);

    if (lang !== null && this.insertValue && this.#inFirstLine(lang)) {
      this.#writeHeading(lang, this.#titleField(lang)?.value ?? '', true);
    }
  }

  // Le titre 1 modifié dans la colonne met le champ titre à jour
  #listen(editor) {
    if (editor.titleInBlocksListening) {
      return;
    }

    editor.titleInBlocksListening = true;
    editor.on('change', () => {
      const lang = this.#langOfEditor(editor);

      if (lang === null || !this.#inFirstLine(lang)) {
        return;
      }

      const heading = this.#leadingHeading(editor.editable().$);
      const field = this.#titleField(lang);

      // `keyup` : à la création, le titre dans le menu et l'adresse de la page se déduisent du titre à la frappe
      if (heading && field && field.value !== heading.textContent) {
        field.value = heading.textContent;
        field.dispatchEvent(new Event('change', {bubbles: true}));
        field.dispatchEvent(new KeyboardEvent('keyup', {bubbles: true}));
      }
    });
  }

  #writeHeading(lang, title, insert) {
    const target = this.#firstTextCell(lang);

    if (!target) {
      return;
    }

    const editor = this.#readyEditor(target.cellContent);

    if (editor) {
      const root = editor.editable().$;
      const heading = this.#leadingHeading(root);

      if (heading) {
        heading.textContent = title;
      } else if (insert && title !== '') {
        root.insertBefore(this.#heading(root.ownerDocument, title), root.firstChild);
      } else {
        return;
      }

      editor.fire('change');

      return;
    }

    const root = this.#parse(target.content ?? '');
    const heading = this.#leadingHeading(root);

    if (heading) {
      heading.textContent = title;
    } else if (insert && title !== '') {
      root.insertBefore(this.#heading(document, title), root.firstChild);
    } else {
      return;
    }

    this.#store(target, root.innerHTML);
  }

  #removeHeading(lang) {
    const target = this.#firstTextCell(lang);
    const title = (this.#titleField(lang)?.value ?? '').trim();

    if (!target) {
      return;
    }

    const editor = this.#readyEditor(target.cellContent);
    const root = editor ? editor.editable().$ : this.#parse(target.content ?? '');
    const heading = this.#leadingHeading(root);

    if (!heading || heading.textContent.trim() !== title) {
      return;
    }

    heading.remove();

    if (editor) {
      editor.fire('change');
    } else {
      this.#store(target, root.innerHTML);
    }
  }

  // version, dans cette langue, de la première colonne de texte des lignes lues dans l'ordre
  #firstTextCell(lang) {
    const grid = Object.values(window.Blocks?.Grids?.grids ?? {})[0];

    for (const line of grid?.lines ?? []) {
      for (const cell of line.cells ?? []) {
        if (cell.contentType === 'text' && cell.versions?.[lang]) {
          return cell.versions[lang];
        }
      }
    }

    return null;
  }

  #langOfEditor(editor) {
    return this.#langs().find((lang) => {
      const cellContent = this.#firstTextCell(lang)?.cellContent;

      return cellContent && `${cellContent.internalId}-textarea` === editor.name;
    }) ?? null;
  }

  #readyEditor(cellContent) {
    const editor = cellContent ? window.CKEDITOR?.instances[`${cellContent.internalId}-textarea`] : null;

    return editor?.status === 'ready' && editor.mode === 'wysiwyg' ? editor : null;
  }

  // premier élément du contenu, s'il est un titre 1 (les paragraphes vides avant lui ne comptent pas ; un paragraphe
  // qui porte une image ou un autre élément n'est pas vide)
  #leadingHeading(root) {
    for (const node of root.children) {
      if (node.tagName === 'H1') {
        return node;
      }

      if (node.tagName !== 'P' || node.textContent.trim() !== '' || node.querySelector(':not(br)')) {
        return null;
      }
    }

    return null;
  }

  #heading(doc, title) {
    const heading = doc.createElement('h1');

    heading.textContent = title;

    return heading;
  }

  #parse(html) {
    const root = document.createElement('div');

    root.innerHTML = html;

    return root;
  }

  // contenu d'une colonne pas encore ouverte dans un éditeur : le modèle, puis le champ du formulaire
  #store(target, html) {
    target.content = html;
    target.parentCell.parentLine.parentGrid.saveToFormInput();
  }

  #inFirstLine(lang) {
    return this.#field(lang, 'titleInBlocks', ':checked')?.value === '1';
  }

  #titleField(lang) {
    return this.#field(lang, 'title');
  }

  #field(lang, name, suffix = '') {
    return this.element.querySelector(`[name="${this.itemValue}[versions][${lang}][${name}]"]${suffix}`);
  }

  #lang(field, name) {
    const match = field.name?.match(new RegExp(`^${this.itemValue}\\[versions\\]\\[([^\\]]+)\\]\\[${name}\\]$`));

    return match ? match[1] : null;
  }

  #langs() {
    return [...this.element.querySelectorAll(`[name^="${this.itemValue}[versions]["][name$="][title]"]`)]
      .map((field) => this.#lang(field, 'title'))
      .filter((lang) => lang !== null);
  }
}
